/**
 * Cross-product correlation helper for Sentry scopes.
 *
 * Sentry stores correlation differently per product: Errors are filtered by
 * scope *tags* (`setTag`), whereas Logs are filtered by scope *attributes*
 * (`setAttribute`, supported since SDK 10.32.0). To make a single hierarchical
 * ID searchable across Errors AND Logs (and, transitively, the traces they
 * link to), it must be written as both. This module is the one place that
 * knows that duality so every surface (realtime-api WS/HTTP, host, node-service)
 * stays in sync.
 *
 * Intentionally free of any Sentry dependency: it operates on a structural
 * scope surface so it can be reused across browser/node SDKs and unit-tested
 * with a plain double.
 */

/**
 * Minimal scope surface this helper needs. Declared structurally (rather than
 * importing Sentry's `Scope`) so call sites can pass a real isolation/global
 * scope or a test double without coupling to the SDK's full type.
 */
export interface CorrelatableScope {
  setTag(key: string, value: string | undefined): unknown;
  /** Present on Sentry SDK >= 10.32.0; omitted on older pinned SDKs. */
  setAttribute?(key: string, value: string | undefined): unknown;
}

/** The hierarchical IDs that can be applied to a scope. */
export interface ScopeCorrelationIds {
  machineId?: string;
  broadcastId?: string;
  playerConnectionId?: string;
  userId?: string;
}

/**
 * Write a correlation ID to a scope as both an error tag and a log attribute.
 * No-op when the value is undefined, so an absent ID is never recorded as an
 * empty/placeholder value.
 */
export const setCorrelationId = (
  scope: CorrelatableScope,
  key: string,
  value: string | undefined,
): void => {
  if (value === undefined) return;
  scope.setTag(key, value);
  scope.setAttribute?.(key, value);
};

/**
 * Clear a correlation ID from a scope. Tags are always cleared; log attributes
 * are cleared only when the scope supports `setAttribute`.
 */
export const clearCorrelationId = (
  scope: CorrelatableScope,
  key: string,
): void => {
  scope.setTag(key, undefined);
  scope.setAttribute?.(key, undefined);
};

/**
 * Apply the hierarchical correlation ID set to a scope (tags + log attributes).
 * Only defined IDs are written. Canonical key names match the cross-surface
 * contract: `machine_id`, `broadcast_id`, `player_connection_id`, `user_id`.
 */
export const applyCorrelationIds = (
  scope: CorrelatableScope,
  ids: ScopeCorrelationIds,
): void => {
  setCorrelationId(scope, 'machine_id', ids.machineId);
  setCorrelationId(scope, 'broadcast_id', ids.broadcastId);
  setCorrelationId(scope, 'player_connection_id', ids.playerConnectionId);
  setCorrelationId(scope, 'user_id', ids.userId);
};
